import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import ApexCharts from 'apexcharts';
import { ToastrService } from 'ngx-toastr';
import { DateUtil } from 'src/app/common/util';
import { DayHeatMap, HeatMapResponse, HourHeatMap } from 'src/app/models/heat-map.model';
import { ResponseObject } from 'src/app/models/response-object.model';
import { WarehouseResponse } from 'src/app/models/warehouse.model';
import { HeatMapService } from 'src/app/services/heat-map.service';
import { WarehouseService } from 'src/app/services/warehouse.service';
export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  dataLabels: ApexDataLabels;
  title: ApexTitleSubtitle;
  plotOptions: ApexPlotOptions;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  tooltip: ApexTooltip;
};

@Component({
  selector: 'app-heat-map-sales',
  templateUrl: './heat-map-sales.component.html',
  styleUrls: ['./heat-map-sales.component.scss']
})
export class HeatMapSalesComponent implements OnInit {
  warehouses: WarehouseResponse[] = [];
  warehouse: number = -1;
  year: number;
  month: number;
  type: number = 1;
  years = [];
  days: { [key: number]: DayHeatMap } = {};
  public chartOptions: Partial<ChartOptions>;


  constructor(
    private warehouseService: WarehouseService,
    private toastService: ToastrService,
    private heatMapService: HeatMapService
  ) {
    var today = new Date();
    this.year = today.getFullYear();
    this.month = today.getMonth() + 1;
  }

  ngOnInit(): void {
    for (let i = 2023; i < this.year + 2; i++) {
      this.years.push(i);
    }
    this.getHeatMap();
    this.getWarehouses();
  }

  drawHeatMap(max) {
    var result = Object.keys(this.days).map((key) => [key, this.days[key]]);
    var series = [];

    // Create a mapping of hours first
    var hourMapping = {};
    result.forEach(element => {
      var hours = Object.keys(element[1].hours).map((key) => [key, element[1].hours[key]]);
      hours.forEach(hour => {
        if (!hourMapping[hour[0]]) {
          hourMapping[hour[0]] = { name: hour[1].display, data: [] };
        }
        let rawValue = hour[1].value; // Este es el valor que queremos mostrar
        let colorValue = rawValue; // Y este el porcentaje

        if (this.type === 2) {
          const day = parseInt(element[0]);
          const dayTotal = this.days[day].total || 1; // evitar división por cero
          colorValue = Math.ceil((rawValue / dayTotal) * 100);
        }

        hourMapping[hour[0]].data.push({
          x: element[0],
          y: colorValue, // este es el valor usado para pintar el color
          rawValue: rawValue // este es el valor que queremos mostrar si type == 2
        });
      });
    });

    const colorScale = this.type === 2 ? {
      ranges: [
        {
          from: 0,
          to: 0,
          name: "0%",
          color: "#00FFFF"
        },
        {
          from: 1,
          to: 20,
          name: "1% - 20%",
          color: "#128FD9"
        },
        {
          from: 21,
          to: 50,
          name: "21% - 50%",
          color: "#FEB019"
        },
        {
          from: 51,
          to: 75,
          name: "51% - 75%",
          color: "#FF7A00"
        },
        {
          from: 76,
          to: 100,
          name: "76% - 100%",
          color: "#FF4560"
        }
      ]
    } : {
      ranges: [
        {
          from: -10000,
          to: 0,
          name: "0",
          color: "#00FFFF"
        },
        {
          from: 1,
          to: 20,
          name: "1 - 20",
          color: "#128FD9"
        },
        {
          from: 21,
          to: 49,
          name: "21 - 49",
          color: "#FFB200"
        },
        {
          from: 49,
          to: 100000000000,
          name: "50 en adelante ",
          color: "#FF0000"
        }
      ]
    };

    const quetzalFormat = new Intl.NumberFormat('es-GT', {
      style: 'currency',  
      currency: 'GTQ',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });


    // Convert hourMapping to series
    series = Object.values(hourMapping);
    this.chartOptions = {
      series: series,
      chart: {
        height: 350,
        type: "heatmap"
      },
      plotOptions: {
        heatmap: {
          shadeIntensity: 0.5,
          colorScale: colorScale
        }
      },
      dataLabels: {
        enabled: false
      },
      title: {
        text: this.type == 1 ? 'Mapa de factura por Bodega' : 'Flujo de dinero por Bodega'
      },
      xaxis: {
        type: 'category',
        title: {
          text: 'Días'  // Title for the x-axis
        }
      },
      yaxis: {
        title: {
          text: 'Horas'  // Title for the y-axis
        }
      },
      tooltip: {
        y: {
          formatter: (val, { seriesIndex, dataPointIndex, w }) => {
            const point = w.config.series[seriesIndex].data[dataPointIndex];
            if (this.type === 2 && point.rawValue !== undefined) {
              return quetzalFormat.format(point.rawValue);
            }
            return point.rawValue !== undefined ? point.rawValue : val;
          }
        }
      },
    };
    var chart = new ApexCharts(document.querySelector("#apex-heat-map"), this.chartOptions);
    chart.render();
    chart.updateSeries(series);
  }

  getHeatMap() {
    if (this.warehouse && this.year && this.month && this.type) {
      var daysInMonth = DateUtil.daysInMonth(this.month - 1, this.year);
      this.days = {};
      for (let i = 1; i <= daysInMonth; i++) {
        this.days[i] = new DayHeatMap(i);
      }
      this.heatMapService.getHeatMap(
        this.warehouse,
        this.year,
        this.month,
        this.type
      ).subscribe({
        next: (response: ResponseObject) => {
          var res = response.data as HeatMapResponse[];
          var max = 0;
          res.forEach(element => {
            const value = element.numInvoices;
            this.days[element.day].hours[element.hourGroup].value = value;

            // Aqui se acumula el total por dia
            if (this.type === 2) {
              this.days[element.day].total = (this.days[element.day].total || 0) + value;
            }

            if (value > max) max = value;
          });
          this.drawHeatMap(max);
        },
        error: (error: HttpErrorResponse) => {
          this.toastService.error(error.error.message);
        }
      })
    }
  }

  getWarehouses() {
    this.warehouseService.getWarehouses().subscribe(
      {
        next: (response: ResponseObject) => {
          this.warehouses = response.data as WarehouseResponse[];
          var all = new WarehouseResponse();
          all.id = -1;
          all.name = "Todas las bodegas";
          this.warehouses.unshift(all);
        },
        error: (error: HttpErrorResponse) => {
          this.toastService.error(error.error.message);
        }
      }
    );
  }

  generateData(count, yrange) {
    var i = 0;
    var series = [];
    while (i < count) {
      var x = "s" + (i + 1).toString();
      var y =
        Math.floor(Math.random() * (yrange.max - yrange.min + 1)) + yrange.min;

      series.push({
        x: x,
        y: y
      });
      i++;
    }
    return series;
  }

  downloadRealValuesCSV() {
    const rows = [];
    const headers = ['Hora \\ Dia'];

    const dayKeys = Object.keys(this.days).map(Number).sort((a, b) => a - b);
    headers.push(...dayKeys.map(d => `Dia ${d}`));
    rows.push(headers);

    const hourGroups = new Set<string>();
    Object.values(this.days).forEach(day => {
      Object.keys(day.hours).forEach(h => hourGroups.add(h));
    });

    const sortedHours = Array.from(hourGroups).sort((a, b) => parseInt(a) - parseInt(b));

    sortedHours.forEach(hourKey => {
      const row = [];
      const display = this.days[dayKeys[0]]?.hours[hourKey]?.display || `Hora ${hourKey}`;
      row.push(display);

      dayKeys.forEach(day => {
        const hour = this.days[day]?.hours[hourKey];
        const val = hour?.value || 0;
        const cleanQuetzalFormat = (amount: number) => {
          return `Q. ${amount.toFixed(2)}`;
        };
        row.push(this.type === 1 ? val : cleanQuetzalFormat(val));
      });

      rows.push(row);
    });

    const csvContent = rows.map(e => e.join(";")).join("\n");

    const BOM = "\uFEFF";
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `mapa_de_calor_venta_${this.year}_${this.month}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
